import {CoinsSections} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/sections.types';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  GenericIconName,
  Icon,
  SPACING,
  Text,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useRef, useState} from 'react';
import {useIsMissionsActive} from '../../utils/useIsMissionsActive';
import {CoinsDisclaimer} from './CoinsDisclaimer';
import {CoinsHistory} from './CoinsHistory';
import {CoinsLatestAchievements} from './CoinsLatestAchievements/CoinsLatestAchievements';
import {CoinsLevels} from './CoinsLevels/CoinsLevels';
import {CoinsMissions} from './CoinsMissions/CoinsMissions';
import {CoinsTabNavigation} from './CoinsTabNavigation/CoinsTabNavigation';
import {CoinsViewAchievements} from './CoinsViewAchievements/CoinsViewAchievements';

// Inset the observation band's top edge by 250px so a section only counts as
// in-view once it has scrolled well past the sticky wallet chrome (header +
// tab strip), not the instant its top peeks below the viewport top. A section
// landed via scrollIntoView (with SectionWrapper's scroll-margin-top: 68px)
// settles comfortably inside this band. The min-in-view-index logic in
// handleSectionInView then picks the topmost-currently-visible section as the
// active one.
const ACTIVE_SECTION_ROOT_MARGIN = '-250px 0px 0px 0px';

export const CoinsWalletContent: React.FC<{sections: CoinsSections[]}> = ({
  sections,
}) => {
  const trackFeature = useTrackFeature();
  const isMissionsActive = useIsMissionsActive();
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const tabNavigationWrapperRef = useRef<HTMLDivElement | null>(null);
  const inViewIndicesRef = useRef<Set<number>>(new Set());

  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  const scrollToSection = (sectionIndex: number) => {
    const target = sectionRefs.current[sectionIndex];
    if (!target) return;

    target.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };
  // IntersectionObserver can flag multiple sections as in-view at once (e.g. a
  // short section and the next one both crossing the band near the top of the
  // page). Callback order is DOM order, so the last-to-fire would win and
  // leave the lower section active. Pick the smallest in-view index instead —
  // sections are rendered top-to-bottom, so that is the visually-topmost one.
  const handleSectionInView = (index: number, inView: boolean) => {
    if (inView) {
      inViewIndicesRef.current.add(index);
    } else {
      inViewIndicesRef.current.delete(index);
    }
    if (inViewIndicesRef.current.size === 0) return;
    setActiveSectionIndex(Math.min(...inViewIndicesRef.current));
  };

  return (
    <Wrapper data-testid="coinsWalletContent">
      {sections.map((section, index) => {
        const registerRef = (el: HTMLDivElement | null) => {
          sectionRefs.current[index] = el;
        };

        switch (section.type?.$case) {
          case 'tabNavigation':
            return (
              <CoinsTabNavigation
                key={`section-${index}`}
                tabs={section.type.tabNavigation.tabs}
                activeSectionIndex={activeSectionIndex}
                onTabClick={scrollToSection}
                wrapperRef={tabNavigationWrapperRef}
              />
            );
          case 'missions':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsMissions {...section.type.missions} />
              </InViewWrapper>
            );
          case 'achievements':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsLatestAchievements {...section.type.achievements} />
              </InViewWrapper>
            );
          case 'viewAchievements':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsViewAchievements {...section.type.viewAchievements} />
              </InViewWrapper>
            );
          case 'deprecatedLevels':
            if (isMissionsActive) return null;
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsLevels {...section.type.deprecatedLevels} />
              </InViewWrapper>
            );
          case 'disclaimer':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsDisclaimer {...section.type.disclaimer} />
              </InViewWrapper>
            );
          case 'history':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsHistory {...section.type.history} />
              </InViewWrapper>
            );
          case 'faq':
            return (
              <InViewWrapper
                key={`section-${index}`}
                sectionType={section.type.$case}
                onInViewChange={inView => handleSectionInView(index, inView)}
                registerRef={registerRef}
              >
                <CoinsFAQ
                  title={section.type.faq.title}
                  description={section.type.faq.description}
                  href={section.type.faq.target.url}
                  onClick={() => {
                    trackFeature({eventType: 'click', name: 'coins_faq_card'});
                  }}
                />
              </InViewWrapper>
            );
          default:
            return null;
        }
      })}
    </Wrapper>
  );
};

type SectionType = NonNullable<CoinsSections['type']>['$case'];

const InViewWrapper = ({
  onInViewChange,
  registerRef,
  sectionType,
  children,
}: React.PropsWithChildren<{
  onInViewChange: (inView: boolean) => void;
  registerRef: (el: HTMLDivElement | null) => void;
  sectionType: SectionType;
}>) => {
  const {ref: inViewRef} = useInViewEffect<HTMLDivElement>(onInViewChange, {
    rootMargin: ACTIVE_SECTION_ROOT_MARGIN,
  });
  // Merge the IntersectionObserver ref (returned as a RefObject by
  // useInViewEffect) with the parent-supplied registerRef so the parent can
  // call scrollIntoView on the section. Writing to inViewRef.current triggers
  // the hook's internal Proxy and starts the observer.
  return (
    <SectionWrapper
      data-section-type={sectionType}
      ref={el => {
        inViewRef.current = el;
        registerRef(el);
      }}
    >
      {children}
    </SectionWrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

// 68px matches the sticky CoinsTabNavigation's rendered height so chip-driven
// scrollIntoView lands flush with the bottom of the strip.
const SectionWrapper = styled.div`
  scroll-margin-top: 68px;

  /* Full-bleed hairline between two adjacent FAQ rows (Help / T&C); the
     negative inline margin cancels the wallet's horizontal padding. */
  &[data-section-type='faq'] + &[data-section-type='faq']::before {
    content: '';
    display: block;
    height: 1px;
    margin-inline: -${SPACING['X.Lg']};
    background-color: ${COLORS['Outline.Grey']};
  }

  /* Whitespace between two adjacent mission tiles. */
  &[data-section-type='missions'] + &[data-section-type='missions'] {
    margin-top: ${SPACING['Y.Md']};
  }

  /* Full-bleed grey band that separates major section groups in the lower
     wallet (above the Help / T&C group and above the About You Coins card). */
  &:not([data-section-type='faq']) + &[data-section-type='faq']::before,
  & + &[data-section-type='disclaimer']::before {
    content: '';
    display: block;
    height: ${SPACING['Y.Md']};
    margin-inline: -${SPACING['X.Lg']};
    background-color: ${COLORS['Fill.Grey.Primary']};
  }
`;

const CoinsFAQ: React.FC<{
  title: string;
  description?: string;
  href?: string;
  onClick?: VoidFunction;
}> = ({title, description, href, onClick}) => {
  return (
    <FAQWrapper data-testid="coinsWalletFAQ">
      <FAQLink
        href={href}
        onClick={onClick}
        variant="secondary"
        aria-label={undefined}
      >
        <FAQTextWrapper>
          <Text size="px16" weight="mediumHeavy">
            {title}
          </Text>
          {description && (
            <Text color="Grey0" size="px12" weight="mediumBold">
              {description}
            </Text>
          )}
        </FAQTextWrapper>
        <Icon asset={GenericIconName.ArrowRight} size="SS" />
      </FAQLink>
    </FAQWrapper>
  );
};

const FAQWrapper = styled.div`
  position: relative;
`;

const FAQLink = styled(Button)`
  &&& {
    width: 100%;
    gap: 4px;
    padding: ${SPACING['Y.Xl']} 0;
    background: transparent;
    height: auto;
    box-shadow: none;
    border-radius: 0;

    > span {
      display: flex;
      align-items: center;
      flex: 1;
      padding: 0;
    }
  }
`;

const FAQTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  text-align: left;
`;
